import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Send, Plus } from "lucide-react";

function uid() { return Math.random().toString(36).slice(2, 12); }

const BRAINS = [
  { id: "gpt", label: "GPT 5.6 Terra", short: "GPT" },
  { id: "claude", label: "Claude Sonnet 5.5", short: "Claude" },
];
const BRAIN_HINT = {
  en: { brain: "Brain", switchHint: "Switch anytime — the conversation continues with the other model." },
  fr: { brain: "Cerveau", switchHint: "Change quand tu veux — la conversation continue avec l'autre modèle." },
};

export default function Coach() {
  const { t, lang } = useLang();
  const bh = BRAIN_HINT[lang] || BRAIN_HINT.en;
  const [brain, setBrain] = useState(() => localStorage.getItem("pp_coach_brain") || "gpt");
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("pp_coach_sid") || uid());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef();

  useEffect(() => { localStorage.setItem("pp_coach_sid", sessionId); }, [sessionId]);
  useEffect(() => { localStorage.setItem("pp_coach_brain", brain); }, [brain]);

  useEffect(() => {
    api.get(`/coach/history/${sessionId}`).then((r) => setMessages(r.data)).catch(() => {});
  }, [sessionId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);

  const send = async () => {
    if (!input.trim() || sending) return;
    const msg = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg, id: uid() }]);
    setSending(true);
    try {
      const { data } = await api.post("/coach/chat", { session_id: sessionId, message: msg, brain });
      setMessages((m) => [...m, { role: "assistant", content: data.reply, brain: data.brain, id: uid() }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Coach unavailable");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="coach-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono-tech tracking-[0.3em] text-emerald-400" data-testid="coach-brain-label">{BRAINS.find((b) => b.id === brain)?.label}</div>
          <h1 className="font-display font-black uppercase text-4xl sm:text-5xl mt-2" data-testid="coach-title">{t.coach.title}</h1>
        </div>
        <Button variant="outline" size="sm" onClick={() => { setSessionId(uid()); setMessages([]); }} data-testid="coach-new-session" className="border-[#1E2638] bg-transparent text-slate-300 hover:bg-[#121721]">
          <Plus className="w-4 h-4 mr-1" /> {t.coach.newSession}
        </Button>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3" data-testid="coach-brain-switch">
        <span className="text-[10px] font-mono-tech uppercase tracking-[0.25em] text-slate-500">{bh.brain}</span>
        <div className="inline-flex rounded-full border border-[#1E2638] bg-[#090C10] p-1">
          {BRAINS.map((b) => (
            <button key={b.id} type="button" onClick={() => setBrain(b.id)} data-testid={`coach-brain-${b.id}`} aria-pressed={brain === b.id}
              className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${brain === b.id ? "bg-[#00F076] text-black" : "text-slate-400 hover:text-white"}`}>
              {b.label}
            </button>
          ))}
        </div>
        <span className="text-xs text-slate-500">{bh.switchHint}</span>
      </div>

      <div className="mt-6 rounded-xl border border-[#1E2638] bg-[#121721] min-h-[60vh] flex flex-col">
        <div className="flex-1 overflow-auto p-4 space-y-4" data-testid="coach-messages">
          {messages.length === 0 && (
            <div className="h-full flex items-center justify-center text-slate-500 text-sm text-center py-16">
              <div>
                <div className="font-display font-bold uppercase text-lg text-slate-300">Start the conversation</div>
                <div className="mt-1">e.g. "Give me a 20-min pre-match activation warmup"</div>
              </div>
            </div>
          )}
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-3 whitespace-pre-wrap text-sm leading-relaxed ${
                m.role === "user"
                  ? "bg-[#00F076] text-black rounded-br-sm"
                  : "bg-[#090C10] border border-[#1E2638] text-slate-100 rounded-bl-sm"
              }`} data-testid={`coach-msg-${m.role}`}>
                {m.content}
                {m.role === "assistant" && m.brain && (
                  <div className="mt-2 text-[10px] font-mono-tech tracking-wider text-slate-500" data-testid="coach-msg-brain">{BRAINS.find((b) => b.id === m.brain)?.short}</div>
                )}
              </div>
            </div>
          ))}
          {sending && <div className="text-xs font-mono-tech tracking-wider text-emerald-400 animate-pulse">{t.coach.thinking}</div>}
          <div ref={bottomRef} />
        </div>

        <div className="border-t border-[#1E2638] p-3 flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={t.coach.placeholder}
            data-testid="coach-input"
            className="bg-[#090C10] border-[#1E2638] text-white"
          />
          <Button onClick={send} disabled={sending || !input.trim()} data-testid="coach-send-btn" className="bg-[#00F076] hover:bg-[#00C963] text-black font-bold">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
